import React, { useEffect, useState } from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { Box, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useToast } from 'use-toast-mui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createMemberPoint } from '../../../api/member-point';
import PointExpireField from '../../point/PointExpireField';
import { getBranchManager } from '../../../api/staff';
import { POINT_TYPE } from '../../../constants/point';
import { MEMBER_ADDITION_INFO_KEY } from '../../../constants/queryKeys';
import CommonModalLayout from '../../../form-components/CommonModalLayout';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommonSegmentedControl from '../../../form-components/CommonSegmentedControl';
import { FormInputDropdown } from '../../../form-components/FormInputDropdown';
import { FormInputPrice } from '../../../form-components/FormInputPrice';
import { FormInputText } from '../../../form-components/FormInputText';
import {
  FieldGroup,
  FieldLabel,
  FieldsBox,
  MemoWrapper,
  PointSuffix,
  PreviewBox,
  PreviewHighlight,
  SegmentWrapper,
} from './UserPointHistoryStyled';

const TYPE_OPTIONS = [
  { value: POINT_TYPE.EARN, label: '적립' },
  { value: POINT_TYPE.DEDUCT, label: '차감' },
];

const MEMO_MAX_LENGTH = 10;

// 빨간색 펄스 glow 애니메이션 — CLAUDE.md 표준 유효성 에러 표시 패턴
const errorPulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
`;

const ErrorFieldWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  flex: 1,
  borderRadius: '4px',
  '& > *': {
    width: '100%',
  },
  ...(hasError && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dc2626 !important',
      borderWidth: '2px !important',
    },
    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

const DEFAULT_VALUES = { staffUserId: '', point: '', memo: '' };

/**
 * 회원 포인트 적립/차감 모달
 * - 저장: createMemberPoint → 성공 시 회원 포인트 관련 쿼리 invalidate
 */
const PointChangeModal = ({ open, onClose, memberId, branchId, token, balance }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [type, setType] = useState(POINT_TYPE.EARN);
  const [errorFields, setErrorFields] = useState({});
  // 지급분 유효기간 { expireDate, noExpire }. 둘 다 비면 지점 규칙. 차감에는 보내지 않는다.
  const [expire, setExpire] = useState({ expireDate: null, noExpire: false });

  const { control, handleSubmit, reset, watch } = useForm({ defaultValues: DEFAULT_VALUES });

  const watchedPoint = watch('point');
  const watchedStaffUserId = watch('staffUserId');

  // 필드 값이 채워지면 해당 에러 상태 해제
  useEffect(() => {
    setErrorFields(prev => {
      const updated = { ...prev };
      if (watchedStaffUserId) delete updated.staffUserId;
      if (watchedPoint) delete updated.point;
      return updated;
    });
  }, [watchedStaffUserId, watchedPoint]);

  const { data: staffOptions = [] } = useQuery({
    queryKey: ['branchManager', branchId],
    queryFn: async () => {
      const data = await getBranchManager(branchId, token);
      return (data || []).map(item => ({ value: item.managerId, label: item.managerName }));
    },
    enabled: open && !!branchId && !!token,
  });

  const createPointMutation = useMutation({
    mutationFn: payload => createMemberPoint(memberId, payload, token),
    onSuccess: () => {
      toast.success(t('포인트가 저장되었습니다'));
      // 회원 포인트 잔액/내역(요약 + PointHistorySection) 쿼리 전체 무효화
      queryClient.invalidateQueries({ queryKey: ['memberPoints', memberId, branchId] });
      // 회원 상세 헤더/프로필의 보유 포인트(addition-info)도 함께 갱신
      queryClient.invalidateQueries({ queryKey: MEMBER_ADDITION_INFO_KEY(memberId, branchId) });
      handleClose();
    },
  });

  const handleClose = () => {
    setErrorFields({});
    setType(POINT_TYPE.EARN);
    reset(DEFAULT_VALUES);
    onClose();
  };

  // 모달 전환(적립↔차감) 시 담당자/포인트/메모 입력값은 그대로 유지
  const handleTypeChange = newType => setType(newType);

  const validateForm = values => {
    const errors = {};
    let pointErrorMessage = '차감할 포인트를 입력해 주세요';
    if (type === POINT_TYPE.EARN) pointErrorMessage = '적립할 포인트를 입력해 주세요';

    if (!values.staffUserId) errors.staffUserId = true;

    const pointNum = Number(values.point);
    const hasValidPoint = values.point !== '' && !Number.isNaN(pointNum) && pointNum > 0;

    if (!hasValidPoint) {
      errors.point = true;
    } else if (type === POINT_TYPE.DEDUCT && pointNum > Number(balance || 0)) {
      errors.point = true;
      pointErrorMessage = '보유 포인트를 초과할 수 없습니다';
    }

    setErrorFields(errors);

    if (Object.keys(errors).length > 0) {
      const errorMessageByField = {
        staffUserId: '담당자를 선택해 주세요',
        point: pointErrorMessage,
      };
      const firstErrorField = Object.keys(errors)[0];
      toast.error(t(errorMessageByField[firstErrorField]));
      return false;
    }
    return true;
  };

  const handleSave = handleSubmit(values => {
    if (!validateForm(values)) return;

    createPointMutation.mutate({
      branchId,
      type,
      point: Number(values.point),
      staffUserId: values.staffUserId,
      memo: values.memo,
      expireDate: type === POINT_TYPE.EARN ? expire.expireDate : undefined,
      noExpire: type === POINT_TYPE.EARN && expire.noExpire ? true : undefined,
    });
  });

  const pointValue = Number(watchedPoint || 0);
  const previewBalance = type === POINT_TYPE.EARN ? Number(balance || 0) + pointValue : Number(balance || 0) - pointValue;
  const previewLabel = type === POINT_TYPE.EARN ? '적립 후 포인트' : '차감 후 포인트';

  return (
    <CommonModalLayout
      open={open}
      onClose={handleClose}
      title={t('포인트')}
      width={480}
      footer={
        <>
          <CommonModalButton variant="cancel" onClick={handleClose}>
            {t('취소')}
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSave} disabled={createPointMutation.isPending}>
            {t('저장')}
          </CommonModalButton>
        </>
      }>
      <SegmentWrapper>
        <CommonSegmentedControl options={TYPE_OPTIONS.map(opt => ({ ...opt, label: t(opt.label) }))} value={type} onChange={handleTypeChange} />
      </SegmentWrapper>

      <FieldsBox>
        <FieldGroup>
          <FieldLabel>{t('담당자')}</FieldLabel>
          <ErrorFieldWrapper hasError={errorFields.staffUserId}>
            <FormInputDropdown
              control={control}
              name="staffUserId"
              options={staffOptions}
              disabledItem={
                <MenuItem disabled value="">
                  {t('담당자를 선택해 주세요')}
                </MenuItem>
              }
            />
          </ErrorFieldWrapper>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>{type === POINT_TYPE.EARN ? t('적립할 포인트') : t('차감할 포인트')}</FieldLabel>
          <ErrorFieldWrapper hasError={errorFields.point}>
            <FormInputPrice
              control={control}
              name="point"
              size="small"
              placeholder={t('숫자 입력')}
              InputProps={{ endAdornment: <PointSuffix>P</PointSuffix> }}
            />
          </ErrorFieldWrapper>
        </FieldGroup>
      </FieldsBox>

      {type === POINT_TYPE.EARN && (
        <PointExpireField branchId={branchId} token={token} onChange={setExpire} resetKey={open} />
      )}

      <PreviewBox>
        {t(previewLabel)} :{' '}
        <PreviewHighlight isOverdraft={previewBalance < 0}>
          &nbsp;{previewBalance.toLocaleString()}P{previewBalance < 0 && ` (${t('잔액 초과')})`}
        </PreviewHighlight>
      </PreviewBox>

      <MemoWrapper>
        <FieldLabel>{t('메모')}</FieldLabel>
        <FormInputText
          control={control}
          name="memo"
          placeholder={t('10자 이내로 작성해 주세요.')}
          size="small"
          inputProps={{ maxLength: MEMO_MAX_LENGTH }}
        />
      </MemoWrapper>
    </CommonModalLayout>
  );
};

export default PointChangeModal;
